// src/components/TraineeRegistrationModal.js
import React, { useState, useEffect } from 'react';
import axios from '../config/axiosConfig';
import { useTranslation } from '../contexts/TranslationContext';
import { 
  FaTimes, FaUser, FaEnvelope, FaPhone, FaCalendar, 
  FaBuilding, FaBriefcase, FaTools, FaCheckCircle,
  FaArrowRight, FaArrowLeft, FaSpinner, FaUserPlus,
  FaAddressCard, FaMoneyBillWave, FaChartLine, FaHeart,
  FaStar, FaUsers, FaHandsHelping, FaCertificate, FaExclamationCircle,
  FaEdit, FaSave, FaInfoCircle
} from 'react-icons/fa';
import './TraineeRegistrationModal.css';

const TraineeRegistrationModal = ({ 
  isOpen, 
  onClose, 
  user, 
  onSuccess,
  editMode = false,
  traineeData = null
}) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [stepErrors, setStepErrors] = useState({});
  const [isEditMode, setIsEditMode] = useState(editMode);
  
  // Form data state
  const [formData, setFormData] = useState({
    // Step 0: Basic Info
    name: '',
    age: '',
    cohort: '',
    department_id: '',
    phone: '',
    email: '',
    placement_location: '',
    competence_skills: '',
    skills: '',
    
    // Step 1: Employment Status
    employment_status: '',
    employment_sector: '',
    company_name: '',
    working_position: '',
    working_location: '',
    mode_of_employment: '',
    years_of_experience: '',
    monthly_income: '',
    contract_start_date: '',
    contract_end_date: '',
    
    // Step 2: Job Secured How
    job_secured_how: '',
    
    // Step 3: Job Relevance
    job_relevance: '',
    
    // Step 4: Job Search Challenges
    job_search_challenges: [],
    
    // Step 5: Entrepreneurship
    entrepreneurship: '',
    entrepreneurship_employed_count: '',
    
    // Step 6: Support Needed
    support_needed: [],
    
    // Step 7: Confidence Improved
    confidence_improved: '',
    
    // Step 8: Financial Support Changed
    financial_support_changed: '',
    
    // Step 9: Financial Stability Rating
    financial_stability_rating: '',
    
    // Step 10: Saved Money
    saved_money: '',
    
    // Step 11: Income Management Confidence
    income_management_confidence: '',
    
    // Step 12: Community Impact
    community_impact: '',
    
    // Step 13: Leadership Involvement
    leadership_involvement: '',
    
    // Education & Bio
    education_level: '',
    location: '',
    bio: ''
  });

  const [errors, setErrors] = useState({});
  // ✅ Live name-duplicate check state
const [nameCheck, setNameCheck] = useState({
  checking: false,
  exists: false,
  existing: null,
  checkedName: ''
});
const [nameCheckTimer, setNameCheckTimer] = useState(null);

  // Fetch departments
  useEffect(() => {
    if (isOpen) {
      fetchDepartments();
      // ✅ Reset name check state each time modal opens
      setNameCheck({ checking: false, exists: false, existing: null, checkedName: '' });
      
      if (editMode && traineeData) {
        populateFormWithTraineeData(traineeData);
      } else {
        if (user?.email) {
          setFormData(prev => ({ ...prev, email: user.email }));
        }
        setIsEditMode(false);
      }
    }
  }, [isOpen, user, editMode, traineeData]);

  const populateFormWithTraineeData = (data) => {
    let jobChallenges = data.job_search_challenges || [];
    let supportNeeded = data.support_needed || [];
    
    try {
      if (typeof jobChallenges === 'string') {
        jobChallenges = JSON.parse(jobChallenges);
      }
      if (typeof supportNeeded === 'string') {
        supportNeeded = JSON.parse(supportNeeded);
      }
    } catch (e) {
      console.warn('Error parsing JSON fields:', e);
    }
    
    setFormData({
      name: data.name || '',
      age: data.age || '',
      cohort: data.cohort || '',
      department_id: data.department_id || '',
      phone: data.phone || '',
      email: data.email || '',
      placement_location: data.placement_location || '',
      competence_skills: data.competence_skills || '',
      skills: data.skills || '',
      employment_status: data.employment_status || '',
      employment_sector: data.employment_sector || '',
      company_name: data.company_name || '',
      working_position: data.working_position || '',
      working_location: data.working_location || '',
      mode_of_employment: data.mode_of_employment || '',
      years_of_experience: data.years_of_experience || '',
      monthly_income: data.monthly_income || '',
      contract_start_date: data.contract_start_date || '',
      contract_end_date: data.contract_end_date || '',
      job_secured_how: data.job_secured_how || '',
      job_relevance: data.job_relevance || '',
      job_search_challenges: Array.isArray(jobChallenges) ? jobChallenges : [],
      entrepreneurship: data.entrepreneurship || '',
      entrepreneurship_employed_count: data.entrepreneurship_employed_count || '',
      support_needed: Array.isArray(supportNeeded) ? supportNeeded : [],
      confidence_improved: data.confidence_improved || '',
      financial_support_changed: data.financial_support_changed || '',
      financial_stability_rating: data.financial_stability_rating || '',
      saved_money: data.saved_money || '',
      income_management_confidence: data.income_management_confidence || '',
      community_impact: data.community_impact || '',
      leadership_involvement: data.leadership_involvement || '',
      education_level: data.education_level || '',
      location: data.location || '',
      bio: data.bio || ''
    });
    
    setIsEditMode(true);
  };

  const fetchDepartments = async () => {
    try {
      const response = await axios.get('https://hsn.urugowoc.org.rw/backend/api/departments');
      setDepartments(response.data || []);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  // Step definitions with translations
  const steps = [
    { id: 'basic', title: t('basicInformation') || 'Basic Information', icon: <FaUser /> },
    { id: 'employment', title: t('employmentStatus') || 'Employment Status', icon: <FaBriefcase /> },
    { id: 'job_secured', title: t('jobSecured') || 'How You Secured Job', icon: <FaHandsHelping /> },
    { id: 'job_relevance', title: t('jobRelevance') || 'Job Relevance', icon: <FaStar /> },
    { id: 'challenges', title: t('jobChallenges') || 'Job Search Challenges', icon: <FaTools /> },
    { id: 'entrepreneurship', title: t('entrepreneurship') || 'Entrepreneurship', icon: <FaUsers /> },
    { id: 'support', title: t('supportNeeded') || 'Support Needed', icon: <FaHeart /> },
    { id: 'confidence', title: t('confidenceImpact') || 'Confidence & Impact', icon: <FaChartLine /> },
    { id: 'financial', title: t('financialSituation') || 'Financial Situation', icon: <FaMoneyBillWave /> },
    { id: 'community', title: t('communityImpact') || 'Community Impact', icon: <FaCertificate /> }
  ];



  // ✅ Check if name already exists in women_profiles
const checkTraineeName = (name) => {
  // Clear any pending timer
  if (nameCheckTimer) {
    clearTimeout(nameCheckTimer);
  }

  const trimmed = (name || '').trim();

  // Reset if too short
  if (trimmed.length < 2) {
    setNameCheck({ checking: false, exists: false, existing: null, checkedName: '' });
    return;
  }

  // Skip if it's the same as what we last checked
  if (trimmed === nameCheck.checkedName && nameCheck.exists !== null) {
    return;
  }

  // Show spinner immediately
  setNameCheck(prev => ({ ...prev, checking: true }));

  const timer = setTimeout(async () => {
    try {
      const params = { name: trimmed };
      // When editing, exclude the current trainee's own record
      if (isEditMode && traineeData?.id) {
        params.exclude_id = traineeData.id;
      }

      const response = await axios.get(
        'https://hsn.urugowoc.org.rw/backend/api/trainee/check-name',
        { params }
      );

      setNameCheck({
        checking: false,
        exists: response.data.exists,
        existing: response.data.existing,
        checkedName: trimmed
      });

      // Also set field-level error so it turns red
      if (response.data.exists) {
        setErrors(prev => ({
          ...prev,
          name: t('nameAlreadyExists') || `A trainee named "${trimmed}" already exists.`
        }));
      } else {
        setErrors(prev => ({ ...prev, name: null }));
      }
    } catch (err) {
      console.error('Name check failed:', err);
      setNameCheck({ checking: false, exists: false, existing: null, checkedName: trimmed });
    }
  }, 450);

  setNameCheckTimer(timer);
};
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      if (name === 'job_search_challenges' || name === 'support_needed') {
        setFormData(prev => {
          const current = prev[name] || [];
          if (checked) {
            return { ...prev, [name]: [...current, value] };
          } else {
            return { ...prev, [name]: current.filter(item => item !== value) };
          }
        });
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
    if (stepErrors[currentStep]) {
      setStepErrors(prev => ({ ...prev, [currentStep]: null }));
    }
  };

  const validateStep = () => {
    const newErrors = {};
    let isValid = true;

    switch(currentStep) {
      case 0: // Basic Info
      const nameValue = formData.name?.trim() || '';
      if (!nameValue) {
        newErrors.name = t('fullNameRequired') || 'Full name is required';
        isValid = false;
      } else if (nameValue.length < 2) {
        newErrors.name = t('nameMinLength') || 'Name must be at least 2 characters long';
        isValid = false;
      } else if (nameCheck.exists) {
        // ✅ Block if duplicate detected
        newErrors.name = t('nameAlreadyExists') || `A trainee named "${nameValue}" already exists.`;
        isValid = false;
      }
      
      if (!formData.department_id) {
        newErrors.department_id = t('selectDepartmentRequired') || 'Please select a department';
        isValid = false;
      }
      break;
      case 1: // Employment Status
        if (!formData.employment_status) {
          newErrors.employment_status = t('selectEmploymentStatus') || 'Please select employment status';
          isValid = false;
        }
        break;
      case 2: // Job Secured
        if (formData.employment_status !== 'unemployed' && !formData.job_secured_how) {
          newErrors.job_secured_how = t('selectJobSecured') || 'Please select how you secured your job';
          isValid = false;
        }
        break;
      default:
        break;
    }

    setErrors(newErrors);
    
    if (!isValid) {
      setStepErrors(prev => ({ ...prev, [currentStep]: t('pleaseFillRequiredFields') || 'Please fill in all required fields' }));
    } else {
      setStepErrors(prev => ({ ...prev, [currentStep]: null }));
    }
    
    return isValid;
  };

  const nextStep = () => {
    if (validateStep()) {
      let nextStepIndex = currentStep + 1;
      
      if (nextStepIndex === 2 && formData.employment_status === 'unemployed') {
        nextStepIndex = 3;
      }
      
      if (nextStepIndex === 3 && formData.employment_status === 'unemployed') {
        nextStepIndex = 4;
      }
      
      if (nextStepIndex === 5 && formData.employment_status === 'unemployed') {
        nextStepIndex = 6;
      }
      
      if (nextStepIndex === 6 && formData.entrepreneurship !== 'yes') {
        nextStepIndex = 7;
      }
      
      setCurrentStep(Math.min(nextStepIndex, steps.length - 1));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const prevStep = () => {
    let prevStepIndex = currentStep - 1;
    
    while (prevStepIndex >= 0) {
      if (prevStepIndex === 2 && formData.employment_status === 'unemployed') {
        prevStepIndex--;
        continue;
      }
      
      if (prevStepIndex === 3 && formData.employment_status === 'unemployed') {
        prevStepIndex--;
        continue;
      }
      
      if (prevStepIndex === 5 && formData.employment_status === 'unemployed') {
        prevStepIndex--;
        continue;
      }
      
      if (prevStepIndex === 6 && formData.entrepreneurship !== 'yes') {
        prevStepIndex--;
        continue;
      }
      
      break;
    }
    
    setCurrentStep(Math.max(prevStepIndex, 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
  
    setLoading(true);
    setError(null);
  
    try {
      const formDataToSend = new FormData();
  
      // All text/select/checkbox fields (matching the database columns)
      const allFields = [
        'name', 'age', 'cohort', 'department_id', 'phone', 'email',
        'placement_location', 'competence_skills', 'skills',
        'education_level', 'location', 'bio',
        'employment_status', 'employment_sector', 'company_name',
        'working_position', 'working_location', 'mode_of_employment',
        'years_of_experience', 'monthly_income',
        'contract_start_date', 'contract_end_date',
        'job_secured_how', 'job_relevance',
        'entrepreneurship', 'entrepreneurship_employed_count',
        'confidence_improved', 'financial_support_changed',
        'financial_stability_rating', 'saved_money',
        'income_management_confidence',
        'community_impact', 'leadership_involvement'
      ];
  
      // Always append every field, even if empty, to ensure the backend receives them
      allFields.forEach(key => {
        let value = formData[key];
        if (value === null || value === undefined) value = '';
        // Array fields must be JSON‑stringified
        if (key === 'job_search_challenges' || key === 'support_needed') {
          value = JSON.stringify(Array.isArray(value) ? value : []);
        }
        formDataToSend.append(key, value);
      });
  
      // Include is_available and is_active – essential for updates
      if (isEditMode && traineeData) {
        // Preserve current values when editing
        formDataToSend.append('is_available', traineeData.is_available ? 'true' : 'false');
        formDataToSend.append('is_active', traineeData.is_active ? 'true' : 'false');
      } else {
        // Default values for new registration
        formDataToSend.append('is_available', 'true');
        formDataToSend.append('is_active', 'true');
      }
  
      // (Optional) Log the payload for debugging
      console.log('📤 Sending FormData:');
      for (let pair of formDataToSend.entries()) {
        console.log(pair[0] + ':', pair[1]);
      }
  
      let response;
  
      if (isEditMode && traineeData?.id) {
        const token = localStorage.getItem('token');
        console.log('🔄 Updating trainee ID:', traineeData.id);
        response = await axios.put(
          `https://hsn.urugowoc.org.rw/backend/api/manager/women-profiles/${traineeData.id}`,
          formDataToSend,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              'Authorization': `Bearer ${token}`
            }
          }
        );
      } else {
        response = await axios.post(
          'https://hsn.urugowoc.org.rw/backend/api/trainee/register',
          formDataToSend,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      }
  
      if (response.data.success || response.data.message) {
        setSubmitted(true);
        setLoading(false);
        if (onSuccess) onSuccess(response.data);
      }
    } catch (error) {
      console.error('❌ Registration error:', error);
      if (error.response) {
        setError(error.response.data?.error || `Server error: ${error.response.status}`);
      } else if (error.request) {
        setError(t('noServerResponse') || 'No response from server. Please check your connection.');
      } else {
        setError(error.message || t('registrationFailed') || 'Registration failed. Please try again.');
      }
      setLoading(false);
    }
  };
  const isUnemployed = formData.employment_status === 'unemployed';
  const isEntrepreneur = formData.entrepreneurship === 'yes';

  const isStepDisabled = (stepIndex) => {
    if (stepIndex === 2 && isUnemployed) return true;
    if (stepIndex === 3 && isUnemployed) return true;
    if (stepIndex === 5 && isUnemployed) return true;
    if (stepIndex === 6 && !isEntrepreneur) return true;
    return false;
  };

  const renderStepContent = () => {
    if (isStepDisabled(currentStep)) {
      const nextAvailable = findNextAvailableStep(currentStep);
      if (nextAvailable !== currentStep) {
        setCurrentStep(nextAvailable);
        return null;
      }
      return renderDisabledStepMessage();
    }

    switch(currentStep) {
      case 0: return renderBasicInfo();
      case 1: return renderEmploymentStatus();
      case 2: return renderJobSecured();
      case 3: return renderJobRelevance();
      case 4: return renderJobChallenges();
      case 5: return renderEntrepreneurship();
      case 6: return renderSupportNeeded();
      case 7: return renderConfidence();
      case 8: return renderFinancial();
      case 9: return renderCommunity();
      default: return null;
    }
  };

  const findNextAvailableStep = (current) => {
    for (let i = current + 1; i < steps.length; i++) {
      if (!isStepDisabled(i)) {
        return i;
      }
    }
    return current;
  };

  const findPrevAvailableStep = (current) => {
    for (let i = current - 1; i >= 0; i--) {
      if (!isStepDisabled(i)) {
        return i;
      }
    }
    return current;
  };

  const renderDisabledStepMessage = () => {
    let message = '';
    if (isUnemployed) {
      message = t('stepSkippedUnemployed') || 'This section is not applicable because you selected "Unemployed". Please proceed to the next available step.';
    } else if (!isEntrepreneur && currentStep === 6) {
      message = t('stepSkippedNotEntrepreneur') || 'This section is not applicable because you are not an entrepreneur. Please proceed to the next available step.';
    }
    
    return (
      <div className="trainee-step-content">
        <div className="trainee-skip-message">
          <FaInfoCircle />
          <p>{message}</p>
          <button 
            className="trainee-btn trainee-btn-primary" 
            onClick={() => {
              const next = findNextAvailableStep(currentStep);
              setCurrentStep(next);
            }}
          >
            {t('skipToNextStep') || 'Skip to Next Step'} <FaArrowRight />
          </button>
        </div>
      </div>
    );
  };

  const renderBasicInfo = () => (
    <div className="trainee-step-content">
      <h3>{isEditMode ? (t('editTraineeProfile') || 'Edit Basic Information') : (t('basicInformation') || 'Basic Information')}</h3>
      <p className="trainee-step-description">
        {isEditMode ? (t('editTraineeProfile') || 'Update the basic information for this trainee.') : (t('basicInformation') || 'Please provide your basic information to get started.')}
      </p>
      
      {stepErrors[0] && (
        <div className="trainee-step-error">
          <FaExclamationCircle /> {stepErrors[0]}
        </div>
      )}
      
      <div className="trainee-form-row">
      <div className="trainee-form-group">
  <label>{t('fullName') || 'Full Name'} *</label>
  <input
    type="text"
    name="name"
    value={formData.name || ''}
    onChange={(e) => {
      const value = e.target.value;
      setFormData(prev => ({ ...prev, name: value }));
      if (errors.name) setErrors(prev => ({ ...prev, name: null }));
      if (stepErrors[currentStep]) setStepErrors(prev => ({ ...prev, [currentStep]: null }));
      // ✅ Trigger live duplicate check
      checkTraineeName(value);
    }}
    onBlur={(e) => {
      // Double-check on blur for safety
      if (e.target.value.trim().length >= 2) {
        checkTraineeName(e.target.value);
      }
    }}
    placeholder={t('enterName') || 'Enter your full name'}
    className={errors.name || nameCheck.exists ? 'error' : ''}
    required
  />

  {/* ✅ Live check feedback */}
  {nameCheck.checking && (
    <small style={{ color: '#718096', display: 'flex', alignItems: 'center', gap: 6 }}>
      <FaSpinner className="trainee-spinning" /> {t('checkingName') || 'Checking name...'}
    </small>
  )}
  {!nameCheck.checking && nameCheck.exists && (
    <small style={{ color: '#f44336', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
      <FaExclamationCircle /> {t('nameAlreadyExists') || `A trainee named "${nameCheck.existing?.name}" already exists.`}
    </small>
  )}
  {!nameCheck.checking && !nameCheck.exists && formData.name.trim().length >= 2 && (
    <small style={{ color: '#4CAF50', display: 'flex', alignItems: 'center', gap: 6 }}>
      <FaCheckCircle /> {t('nameAvailable') || 'Name is available'}
    </small>
  )}
  {errors.name && !nameCheck.exists && (
    <span className="trainee-error-text">{errors.name}</span>
  )}
</div>
        <div className="trainee-form-group">
          <label>{t('age') || 'Age'}</label>
          <input
            type="number"
            name="age"
            value={formData.age}
            onChange={handleInputChange}
            placeholder={t('enterAge') || 'Your age'}
            min="18"
            max="100"
          />
        </div>
      </div>

      <div className="trainee-form-row">
        <div className="trainee-form-group">
          <label>{t('cohort') || 'Cohort'}</label>
          <select
            name="cohort"
            value={formData.cohort}
            onChange={handleInputChange}
          >
            <option value="">{t('selectCohort') || 'Select cohort'}</option>
            <option value="Cohort 1">Cohort 1</option>
            <option value="Cohort 2">Cohort 2</option>
            <option value="Cohort 3">Cohort 3</option>
            <option value="Cohort 4">Cohort 4</option>
            <option value="Cohort 5">Cohort 5</option>
          </select>
        </div>
        <div className="trainee-form-group">
          <label>{t('selectDepartment') || 'Department'} *</label>
          <select
            name="department_id"
            value={formData.department_id}
            onChange={handleInputChange}
            className={errors.department_id ? 'error' : ''}
          >
            <option value="">{t('selectDepartment') || 'Select department'}</option>
            {departments.map(dept => (
              <option key={dept.id} value={dept.id}>{dept.name}</option>
            ))}
          </select>
          {errors.department_id && <span className="trainee-error-text">{errors.department_id}</span>}
        </div>
      </div>

      <div className="trainee-form-row">
        <div className="trainee-form-group">
          <label>{t('email') || 'Email'} ({t('optional') || 'Optional'})</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder={t('enterEmail') || 'your@email.com (optional)'}
          />
          <small>{t('emailOptional') || 'Email is optional but recommended for communication'}</small>
        </div>
        <div className="trainee-form-group">
          <label>{t('phone') || 'Phone'}</label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            placeholder={t('enterPhone') || '+250 78X XXX XXX'}
          />
        </div>
      </div>

      <div className="trainee-form-group">
        <label>{t('placementLocation') || 'Placement Location'}</label>
        <input
          type="text"
          name="placement_location"
          value={formData.placement_location}
          onChange={handleInputChange}
          placeholder={t('enterPlacementLocation') || 'City, District, Country'}
        />
      </div>

      <div className="trainee-form-group">
        <label>{t('competenceSkills') || 'Competence Skills'} ({t('commaSeparated') || 'comma-separated'})</label>
        <input
          type="text"
          name="competence_skills"
          value={formData.competence_skills}
          onChange={handleInputChange}
          placeholder={t('enterCompetenceSkills') || 'e.g., Computer Skills, Decision Making, Leadership'}
        />
        <small>{t('separateWithCommas') || 'Separate multiple skills with commas'}</small>
      </div>
    </div>
  );

  const renderEmploymentStatus = () => (
    <div className="trainee-step-content">
      <h3>{t('employmentStatus') || 'Employment Status'}</h3>
      <p className="trainee-step-description">{t('employmentStatusDesc') || 'Tell us about your current employment situation.'}</p>
      
      {stepErrors[1] && (
        <div className="trainee-step-error">
          <FaExclamationCircle /> {stepErrors[1]}
        </div>
      )}

      <div className="trainee-form-group">
        <label>{t('employmentStatus') || 'Employment Status'} *</label>
        <select
          name="employment_status"
          value={formData.employment_status}
          onChange={handleInputChange}
          className={errors.employment_status ? 'error' : ''}
        >
          <option value="">{t('selectStatus') || 'Select status'}</option>
          <option value="self_employed">{t('selfEmployed') || 'Self Employed'}</option>
          <option value="unemployed">{t('unemployed') || 'Unemployed'}</option>
          <option value="employed_by_company">{t('employedByCompany') || 'Employed by Company'}</option>
          <option value="professional_internship">{t('professionalInternship') || 'Professional Internship'}</option>
          <option value="casual_job">{t('casualJob') || 'Casual Job'}</option>  
          <option value="studying">{t('continueStudying') || 'Continue Studying'}</option>   {/* ← ADD THIS LINE */}
        </select>
        {errors.employment_status && <span className="trainee-error-text">{errors.employment_status}</span>}
      </div>

      {!isUnemployed && (
        <>
          <div className="trainee-form-group">
            <label>{t('employmentSector') || 'Employment Sector'}</label>
            <select
              name="employment_sector"
              value={formData.employment_sector}
              onChange={handleInputChange}
            >
              <option value="">{t('selectSector') || 'Select sector'}</option>
              <option value="public">{t('publicSector') || 'Public'}</option>
              <option value="private">{t('privateSector') || 'Private'}</option>
              <option value="ngo">{t('ngo') || 'NGO'}</option>
              <option value="other">{t('other') || 'Other'}</option>
            </select>
          </div>

          <div className="trainee-form-row">
            <div className="trainee-form-group">
              <label>{t('companyName') || 'Company Name'}</label>
              <input
                type="text"
                name="company_name"
                value={formData.company_name}
                onChange={handleInputChange}
                placeholder={t('enterCompanyName') || 'Company/organization name'}
              />
            </div>
            <div className="trainee-form-group">
              <label>{t('workingPosition') || 'Working Position'}</label>
              <input
                type="text"
                name="working_position"
                value={formData.working_position}
                onChange={handleInputChange}
                placeholder={t('enterWorkingPosition') || 'Job title'}
              />
            </div>
          </div>

          <div className="trainee-form-row">
            <div className="trainee-form-group">
              <label>{t('workingLocation') || 'Working Location'}</label>
              <input
                type="text"
                name="working_location"
                value={formData.working_location}
                onChange={handleInputChange}
                placeholder={t('enterWorkingLocation') || 'Work location'}
              />
            </div>
            <div className="trainee-form-group">
              <label>{t('modeOfEmployment') || 'Mode of Employment'}</label>
              <select
                name="mode_of_employment"
                value={formData.mode_of_employment}
                onChange={handleInputChange}
              >
                <option value="">{t('selectMode') || 'Select mode'}</option>
                <option value="permanent">{t('permanent') || 'Permanent Job'}</option>
                <option value="internship">{t('internship') || 'Internship'}</option>
                <option value="part_time">{t('partTime') || 'Part Time Contract'}</option>
                <option value="volunteer">{t('volunteer') || 'Volunteer'}</option>
              </select>
            </div>
          </div>

          <div className="trainee-form-row">
            <div className="trainee-form-group">
              <label>{t('yearsOfExperience') || 'Years of Experience'}</label>
              <input
                type="number"
                name="years_of_experience"
                value={formData.years_of_experience}
                onChange={handleInputChange}
                placeholder={t('enterYears') || 'Years'}
                min="0"
              />
            </div>
            <div className="trainee-form-group">
              <label>{t('monthlyIncome') || 'Approximate Monthly Income'}</label>
              <select
                name="monthly_income"
                value={formData.monthly_income}
                onChange={handleInputChange}
              >
                <option value="">{t('selectIncome') || 'Select range'}</option>
                <option value="10k-50k">10,000 - 50,000 RWF</option>
                <option value="60k-100k">60,000 - 100,000 RWF</option>
                <option value="100k-200k">100,000 - 200,000 RWF</option>
                <option value="above-200k">{t('above200k') || 'Above 200,000 RWF'}</option>
              </select>
            </div>
          </div>

          <div className="trainee-form-row">
            <div className="trainee-form-group">
              <label>{t('contractStartDate') || 'Contract Start Date'}</label>
              <input
                type="date"
                name="contract_start_date"
                value={formData.contract_start_date}
                onChange={handleInputChange}
              />
            </div>
            <div className="trainee-form-group">
              <label>{t('contractEndDate') || 'Contract End Date'}</label>
              <input
                type="date"
                name="contract_end_date"
                value={formData.contract_end_date}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </>
      )}

      {isUnemployed && (
        <div className="trainee-info-message">
          <FaInfoCircle />
          <p>{t('unemployedInfo') || 'You selected "Unemployed". Additional employment details are not required.'}</p>
        </div>
      )}
    </div>
  );

  const renderJobSecured = () => {
    if (isUnemployed) {
      return renderDisabledStepMessage();
    }

    return (
      <div className="trainee-step-content">
        <h3>{t('jobSecured') || 'How Did You Secure Your Current Job?'}</h3>
        
        {stepErrors[2] && (
          <div className="trainee-step-error">
            <FaExclamationCircle /> {stepErrors[2]}
          </div>
        )}
        
        <div className="trainee-form-group">
          <select
            name="job_secured_how"
            value={formData.job_secured_how}
            onChange={handleInputChange}
            className={errors.job_secured_how ? 'error' : ''}
          >
            <option value="">{t('selectJobSecured') || 'Select how you secured your job'}</option>
            <option value="woc_placement">{t('wocPlacement') || 'Through WOC Job Placement Support'}</option>
            <option value="woc_internship">{t('wocInternship') || 'Internship/Industrial Attachment at WOC'}</option>
            <option value="woc_recommendation">{t('wocRecommendation') || 'Recommendation from WOC Trainers'}</option>
            <option value="family_friends">{t('familyFriends') || 'Family/Friends'}</option>
            <option value="social_media">{t('socialMedia') || 'Social Media'}</option>
            <option value="online_portal">{t('onlinePortal') || 'Online Job Portal'}</option>
            <option value="walk_in">{t('walkIn') || 'Walk-in Application'}</option>
            <option value="newspaper">{t('newspaper') || 'Newspaper Advertisement'}</option>
            <option value="started_business">{t('startedBusiness') || 'Started My Own Business'}</option>
          </select>
          {errors.job_secured_how && <span className="trainee-error-text">{errors.job_secured_how}</span>}
        </div>
      </div>
    );
  };

  const renderJobRelevance = () => {
    if (isUnemployed) {
      return renderDisabledStepMessage();
    }

    return (
      <div className="trainee-step-content">
        <h3>{t('jobRelevance') || 'Job Relevance'}</h3>
        <p className="trainee-step-description">{t('jobRelevanceDesc') || 'Is your current job related to the training you received at WOC?'}</p>

        <div className="trainee-form-group">
          <select
            name="job_relevance"
            value={formData.job_relevance}
            onChange={handleInputChange}
          >
            <option value="">{t('selectRelevance') || 'Select relevance'}</option>
            <option value="directly_related">{t('directlyRelated') || 'Directly Related'}</option>
            <option value="somewhat_related">{t('somewhatRelated') || 'Somewhat Related'}</option>
            <option value="not_related">{t('notRelated') || 'Not Related'}</option>
          </select>
        </div>
      </div>
    );
  };

  const renderJobChallenges = () => {
    if (!isUnemployed) {
      return (
        <div className="trainee-step-content">
          <div className="trainee-skip-message">
            <FaInfoCircle />
            <p>{t('stepSkippedNotUnemployed') || 'You are not unemployed. This step is not applicable.'}</p>
            <button 
              className="trainee-btn trainee-btn-primary" 
              onClick={() => {
                const next = findNextAvailableStep(currentStep);
                setCurrentStep(next);
              }}
            >
              {t('skipToNextStep') || 'Skip to Next Step'} <FaArrowRight />
            </button>
          </div>
        </div>
      );
    }

    const challenges = [
      t('lackJobOpportunities') || 'Lack of job opportunities',
      t('lackWorkExperience') || 'Lack of work experience',
      t('insufficientSkills') || 'Insufficient skills',
      t('familyResponsibilities') || 'Family responsibilities',
      t('continuingEducation') || 'Continuing education',
      t('healthReasons') || 'Health reasons',
      t('lackStartupCapital') || 'Lack of startup capital',
      t('locationTransport') || 'Location/transport challenges',
      t('other') || 'Other'
    ];

    return (
      <div className="trainee-step-content">
        <h3>{t('jobChallenges') || 'Job Search Challenges'}</h3>
        <p className="trainee-step-description">{t('jobChallengesDesc') || 'What are the main reasons for your unemployment? (Check all that apply)'}</p>

        <div className="trainee-checkbox-group">
          {challenges.map(challenge => (
            <label key={challenge} className="trainee-checkbox-label">
              <input
                type="checkbox"
                name="job_search_challenges"
                value={challenge}
                checked={formData.job_search_challenges.includes(challenge)}
                onChange={handleInputChange}
              />
              <span>{challenge}</span>
            </label>
          ))}
        </div>
      </div>
    );
  };

  const renderEntrepreneurship = () => {
    if (isUnemployed) {
      return renderDisabledStepMessage();
    }

    return (
      <div className="trainee-step-content">
        <h3>{t('entrepreneurship') || 'Entrepreneurship'}</h3>
        <p className="trainee-step-description">{t('entrepreneurshipDesc') || 'Have you started your own business after graduating?'}</p>

        <div className="trainee-form-group">
          <select
            name="entrepreneurship"
            value={formData.entrepreneurship}
            onChange={handleInputChange}
          >
            <option value="">{t('selectOption') || 'Select option'}</option>
            <option value="yes">{t('yes') || 'Yes'}</option>
            <option value="no">{t('no') || 'No'}</option>
          </select>
        </div>

        {isEntrepreneur && (
          <div className="trainee-form-group">
            <label>{t('employeesEmployed') || 'How many people have you employed?'}</label>
            <select
              name="entrepreneurship_employed_count"
              value={formData.entrepreneurship_employed_count}
              onChange={handleInputChange}
            >
              <option value="">{t('selectNumber') || 'Select number'}</option>
              <option value="none">{t('none') || 'None'}</option>
              <option value="1-2">1–2</option>
              <option value="3-5">3–5</option>
              <option value="more-than-5">{t('moreThan5') || 'More than 5'}</option>
            </select>
          </div>
        )}
      </div>
    );
  };

  const renderSupportNeeded = () => {
    if (!isEntrepreneur) {
      return renderDisabledStepMessage();
    }

    const supports = [
      t('accessFinance') || 'Access to finance',
      t('businessMentorship') || 'Business mentorship',
      t('marketingSupport') || 'Marketing support',
      t('advancedTraining') || 'Advanced technical training',
      t('equipment') || 'Equipment',
      t('marketLinkage') || 'Market linkage',
      t('businessRegistration') || 'Business registration support',
      t('other') || 'Other'
    ];

    return (
      <div className="trainee-step-content">
        <h3>{t('supportNeeded') || 'Support Needed'}</h3>
        <p className="trainee-step-description">{t('supportNeededDesc') || 'What support would help you grow your business? (Check all that apply)'}</p>

        <div className="trainee-checkbox-group">
          {supports.map(support => (
            <label key={support} className="trainee-checkbox-label">
              <input
                type="checkbox"
                name="support_needed"
                value={support}
                checked={formData.support_needed.includes(support)}
                onChange={handleInputChange}
              />
              <span>{support}</span>
            </label>
          ))}
        </div>
      </div>
    );
  };

  const renderConfidence = () => (
    <div className="trainee-step-content">
      <h3>{t('confidenceImpact') || 'Confidence & Impact'}</h3>
      <p className="trainee-step-description">{t('confidenceDesc') || 'To what extent has the training at WOC improved your confidence and ability to make independent life decisions?'}</p>

      <div className="trainee-form-group">
        <select
          name="confidence_improved"
          value={formData.confidence_improved}
          onChange={handleInputChange}
        >
          <option value="">{t('selectConfidence') || 'Select extent'}</option>
          <option value="very_great">{t('veryGreatExtent') || 'To a very great extent'}</option>
          <option value="great">{t('greatExtent') || 'To a great extent'}</option>
          <option value="moderate">{t('moderateExtent') || 'To a moderate extent'}</option>
          <option value="small">{t('smallExtent') || 'To a small extent'}</option>
          <option value="not_at_all">{t('notAtAll') || 'Not at all'}</option>
        </select>
      </div>
    </div>
  );

  const renderFinancial = () => (
    <div className="trainee-step-content">
      <h3>{t('financialSituation') || 'Financial Situation'}</h3>
      
      <div className="trainee-form-group">
        <label>{t('financialSupportChanged') || 'How has your ability to support yourself financially changed?'}</label>
        <select
          name="financial_support_changed"
          value={formData.financial_support_changed}
          onChange={handleInputChange}
        >
          <option value="">{t('selectOption') || 'Select option'}</option>
          <option value="fully_independent">{t('fullyIndependent') || 'I am now fully financially independent'}</option>
          <option value="significantly_contribute">{t('significantlyContribute') || 'I contribute significantly to my household income'}</option>
          <option value="occasionally_contribute">{t('occasionallyContribute') || 'I contribute occasionally to household income'}</option>
          <option value="not_changed">{t('notChanged') || 'My financial situation has not changed'}</option>
          <option value="worsened">{t('worsened') || 'My financial situation has worsened'}</option>
        </select>
      </div>

      <div className="trainee-form-group">
        <label>{t('financialStability') || 'Compared to before attending WOC, how would you rate your current financial stability?'}</label>
        <select
          name="financial_stability_rating"
          value={formData.financial_stability_rating}
          onChange={handleInputChange}
        >
          <option value="">{t('selectRating') || 'Select rating'}</option>
          <option value="much_better">{t('muchBetter') || 'Much better'}</option>
          <option value="better">{t('better') || 'Better'}</option>
          <option value="same">{t('same') || 'About the same'}</option>
          <option value="worse">{t('worse') || 'Worse'}</option>
          <option value="much_worse">{t('muchWorse') || 'Much worse'}</option>
        </select>
      </div>

      <div className="trainee-form-group">
        <label>{t('savedMoney') || 'Have you been able to save money regularly since completing your training?'}</label>
        <select
          name="saved_money"
          value={formData.saved_money}
          onChange={handleInputChange}
        >
          <option value="">{t('selectOption') || 'Select option'}</option>
          <option value="yes_every_month">{t('yesEveryMonth') || 'Yes, every month'}</option>
          <option value="yes_occasionally">{t('yesOccasionally') || 'Yes, occasionally'}</option>
          <option value="no_but_plan">{t('noButPlan') || 'No, but I plan to'}</option>
          <option value="no">{t('no') || 'No'}</option>
        </select>
      </div>

      <div className="trainee-form-group">
        <label>{t('incomeManagement') || 'How confident are you in managing your personal income?'}</label>
        <select
          name="income_management_confidence"
          value={formData.income_management_confidence}
          onChange={handleInputChange}
        >
          <option value="">{t('selectConfidence') || 'Select confidence level'}</option>
          <option value="very_confident">{t('veryConfident') || 'Very confident'}</option>
          <option value="confident">{t('confident') || 'Confident'}</option>
          <option value="moderately_confident">{t('moderatelyConfident') || 'Moderately confident'}</option>
          <option value="slightly_confident">{t('slightlyConfident') || 'Slightly confident'}</option>
          <option value="not_confident">{t('notConfident') || 'Not confident at all'}</option>
        </select>
      </div>
    </div>
  );

  const renderCommunity = () => (
    <div className="trainee-step-content">
      <h3>{t('communityImpact') || 'Community Impact'}</h3>

      <div className="trainee-form-group">
        <label>{t('communityImpactDesc') || 'How would you describe the impact of WOC training on your life and your community?'}</label>
        <select
          name="community_impact"
          value={formData.community_impact}
          onChange={handleInputChange}
        >
          <option value="">{t('selectImpact') || 'Select impact level'}</option>
          <option value="transformational">{t('transformational') || 'Transformational'}</option>
          <option value="significant_positive">{t('significantPositive') || 'Significant positive impact'}</option>
          <option value="moderate_positive">{t('moderatePositive') || 'Moderate positive impact'}</option>
          <option value="slight_positive">{t('slightPositive') || 'Slight positive impact'}</option>
          <option value="no_impact">{t('noImpact') || 'No noticeable impact'}</option>
        </select>
      </div>

      <div className="trainee-form-group">
        <label>{t('leadershipInvolvement') || 'Have you become more involved in leadership or decision-making within your family or community after graduating from WOC?'}</label>
        <select
          name="leadership_involvement"
          value={formData.leadership_involvement}
          onChange={handleInputChange}
        >
          <option value="">{t('selectOption') || 'Select option'}</option>
          <option value="significantly">{t('significantly') || 'Yes, significantly'}</option>
          <option value="to_some_extent">{t('toSomeExtent') || 'Yes, to some extent'}</option>
          <option value="no_change">{t('noChange') || 'No change'}</option>
          <option value="less_involved">{t('lessInvolved') || 'Less involved than before'}</option>
        </select>
      </div>
    </div>
  );

  if (!isOpen) return null;

  if (submitted) {
    return (
      <div className="trainee-modal-overlay" onClick={onClose}>
        <div className="trainee-modal trainee-modal-success" onClick={e => e.stopPropagation()}>
          <button className="trainee-modal-close" onClick={onClose}>
            <FaTimes />
          </button>
          <div className="trainee-success-content">
            <FaCheckCircle className="trainee-success-icon" />
            <h2>{isEditMode ? (t('profileUpdatedSuccess') || 'Profile Updated!') : (t('registrationComplete') || 'Registration Complete!')}</h2>
            <p>{isEditMode ? (t('profileUpdatedSuccess') || 'The trainee profile has been updated successfully.') : (t('thankYouRegistration') || 'Thank you for completing your registration. Your information has been saved successfully.')}</p>
            <p className="trainee-success-sub">{isEditMode ? (t('profileUpdatedSuccess') || 'The changes have been saved.') : (t('registrationSuccessSub') || 'You can now access all features of the platform.')}</p>
            <button className="trainee-btn trainee-btn-primary" onClick={onClose}>
              {t('continue') || 'Continue'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isCurrentStepDisabled = isStepDisabled(currentStep);

  return (
    <div className="trainee-modal-overlay" onClick={onClose}>
      <div className="trainee-modal" onClick={e => e.stopPropagation()}>
        <div className="trainee-modal-header">
          <div className="trainee-modal-header-left">
            <h2>
              {isEditMode ? (
                <>
                  <FaEdit /> {t('editTraineeProfile') || 'Edit Trainee Profile'}
                </>
              ) : (
                <>
                  <FaUserPlus /> {t('traineeRegistration') || 'Trainee Registration'}
                </>
              )}
            </h2>
            {isEditMode && traineeData && (
              <span className="trainee-edit-badge">
                {t('editing') || 'Editing:'} {traineeData.name}
              </span>
            )}
          </div>
          <button className="trainee-modal-close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className="trainee-progress-bar">
          <div className="trainee-progress-track">
            {steps.map((step, index) => {
              const isDisabled = isStepDisabled(index);
              return (
                <div 
                  key={step.id}
                  className={`trainee-progress-step ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''} ${isDisabled ? 'disabled' : ''}`}
                  onClick={() => !isDisabled && index < currentStep && setCurrentStep(index)}
                >
                  <span className="trainee-step-number">{index + 1}</span>
                  <span className="trainee-step-label">{step.title}</span>
                  {isDisabled && <span className="trainee-step-disabled-label">{t('skipped') || 'Skipped'}</span>}
                </div>
              );
            })}
          </div>
          <div 
            className="trainee-progress-fill" 
            style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
          />
        </div>

        {error && (
          <div className="trainee-error-banner">
            <span>{error}</span>
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        <div className="trainee-modal-body">
          {renderStepContent()}
        </div>

        <div className="trainee-modal-footer">
          <div className="trainee-footer-left">
            <span className="trainee-step-counter">
              {t('step') || 'Step'} {currentStep + 1} {t('of') || 'of'} {steps.length}
            </span>
            {isCurrentStepDisabled && (
              <span className="trainee-step-skipped-badge">
                <FaInfoCircle /> {t('notApplicable') || 'Not applicable'}
              </span>
            )}
          </div>
          <div className="trainee-footer-right">
            {currentStep > 0 && (
              <button 
                className="trainee-btn trainee-btn-outline" 
                onClick={prevStep}
                disabled={loading}
              >
                <FaArrowLeft /> {t('previous') || 'Previous'}
              </button>
            )}
            
            {currentStep < steps.length - 1 ? (
              <button 
                className="trainee-btn trainee-btn-primary" 
                onClick={nextStep}
                disabled={loading}
              >
                {t('next') || 'Next'} <FaArrowRight />
              </button>
            ) : (
              <button 
              className="trainee-btn trainee-btn-success" 
              onClick={handleSubmit}
              disabled={loading || nameCheck.exists || nameCheck.checking}
            >
                {loading ? <FaSpinner className="trainee-spinning" /> : <FaCheckCircle />}
                {loading ? (t('submitting') || 'Submitting...') : (isEditMode ? (t('updateProfile') || 'Update Profile') : (t('submit') || 'Complete Registration'))}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TraineeRegistrationModal;